import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { FaFilm, FaPlay } from 'react-icons/fa';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import tmdbApi from '../../api/tmdbApi';
import './magazine-trailer-section.scss';

const TMDB_KEY = '0623e0d232ae90274f2d77494ac4ce50';

function extractYouTubeId(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
}

async function fetchTrailerKey(item) {
    // 1. PHP DB
    if (item.id) {
        try {
            const r = await fetch(`/movies-trailer-api.php?movieId=${item.id}`);
            const d = await r.json();
            const k = extractYouTubeId(d?.data?.trailerUrl);
            if (k) return k;
        } catch {}
    }
    // 2. item.trailerUrl
    const stored = extractYouTubeId(item.trailerUrl);
    if (stored) return stored;

    // 3. TMDB movie
    try {
        const res = await tmdbApi.search('movie', { params: { query: item.title } });
        if (res.results?.length > 0) {
            const vr = await fetch(`https://api.themoviedb.org/3/movie/${res.results[0].id}/videos?api_key=${TMDB_KEY}`);
            const vd = await vr.json();
            const t = vd.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube') || vd.results?.find(v => v.site === 'YouTube');
            if (t) return t.key;
        }
    } catch {}

    // 4. TMDB tv
    try {
        const res = await tmdbApi.search('tv', { params: { query: item.title } });
        if (res.results?.length > 0) {
            const vr = await fetch(`https://api.themoviedb.org/3/tv/${res.results[0].id}/videos?api_key=${TMDB_KEY}`);
            const vd = await vr.json();
            const t = vd.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube') || vd.results?.find(v => v.site === 'YouTube');
            if (t) return t.key;
        }
    } catch {}

    return null;
}

// ── Trailer modal portal ──────────────────────────────────────────
function TrailerModal({ item, onClose }) {
    const [trailerKey, setTrailerKey] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTrailerKey(item)
            .then(k => setTrailerKey(k))
            .finally(() => setLoading(false));

        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [item, onClose]);

    return ReactDOM.createPortal(
        <div className="mts-overlay" onClick={onClose}>
            <div className="mts-modal" onClick={e => e.stopPropagation()}>
                <div className="mts-modal__head">
                    <span className="mts-modal__label"><FaFilm size={13} /> Trailer</span>
                    <h3 className="mts-modal__title">{item.title}</h3>
                    <button className="mts-modal__close" onClick={onClose}><IoClose size={20} /></button>
                </div>
                {loading ? (
                    <div className="mts-modal__state"><div className="mts-spinner" /><p>Đang tải...</p></div>
                ) : trailerKey ? (
                    <div className="mts-modal__player">
                        <iframe
                            src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&rel=0`}
                            allow="autoplay; encrypted-media; fullscreen"
                            allowFullScreen
                            title={`Trailer — ${item.title}`}
                        />
                    </div>
                ) : (
                    <div className="mts-modal__state"><p>Không tìm thấy trailer.</p></div>
                )}
            </div>
        </div>,
        document.body
    );
}

// ── Big card (left, 2fr) ──────────────────────────────────────────
function BigCard({ item, onTrailer }) {
    const banner = item.thumbnailUrl || item.thumbUrl || item.backdropUrl || item.posterUrl || item.imageUrl;
    const poster = item.posterUrl || item.imageUrl || banner;
    const genres = (item.genres || []).slice(0, 2).map(g => g.name).join(' · ');
    const score  = item.averageRating > 0 ? Number(item.averageRating).toFixed(1) : null;
    const eps    = item.episodeCount > 1 ? `${item.episodeCount} tập` : null;
    const watchUrl = `/watch/${item.slug}/${encryptId(1)}`;

    return (
        <div className="mts-card mts-card--big" onClick={() => onTrailer(item)}>
            <div className="mts-card__img-wrap">
                <img src={banner} alt={item.title} loading="lazy" decoding="async"
                    onError={e => { if (e.target.src !== poster) e.target.src = poster; }} />
                <div className="mts-card__gradient" />
                <div className="mts-card__rank">#1</div>
            </div>
            <div className="mts-card__body">
                {genres && <div className="mts-card__genre">{genres}</div>}
                <h3 className="mts-card__title">{item.title}</h3>
                {item.description && (
                    <p className="mts-card__desc"
                        dangerouslySetInnerHTML={{ __html: item.description.replace(/<[^>]+>/g, '') }} />
                )}
                <div className="mts-card__meta">
                    {item.releaseYear && <span>{item.releaseYear}</span>}
                    {score && <span className="mts-card__score">⭐ {score}</span>}
                    {eps && <span>{eps}</span>}
                </div>
                <div className="mts-card__actions">
                    <button className="mts-btn mts-btn--trailer" onClick={e => { e.stopPropagation(); onTrailer(item); }}>
                        <FaFilm size={12} /> Xem trailer
                    </button>
                    <Link to={watchUrl} className="mts-btn mts-btn--watch"
                        onClick={e => e.stopPropagation()}>
                        <FaPlay size={10} /> Xem phim
                    </Link>
                </div>
            </div>
        </div>
    );
}

// ── Small card (right column) ─────────────────────────────────────
function SmallCard({ item, rank, onTrailer }) {
    const banner = item.thumbnailUrl || item.thumbUrl || item.backdropUrl || item.posterUrl || item.imageUrl;
    const poster = item.posterUrl || item.imageUrl || banner;
    const genres = (item.genres || []).slice(0, 1).map(g => g.name).join('');
    const score  = item.averageRating > 0 ? Number(item.averageRating).toFixed(1) : null;
    const eps    = item.episodeCount > 1 ? `${item.episodeCount} tập` : null;

    return (
        <div className="mts-card mts-card--small" onClick={() => onTrailer(item)}>
            <div className="mts-card__img-wrap">
                <img src={banner} alt={item.title} loading="lazy" decoding="async"
                    onError={e => { if (e.target.src !== poster) e.target.src = poster; }} />
                <div className="mts-card__gradient" />
                <div className="mts-card__rank">#{rank}</div>
            </div>
            <div className="mts-card__body">
                {genres && <div className="mts-card__genre">{genres}</div>}
                <h3 className="mts-card__title">{item.title}</h3>
                <div className="mts-card__meta">
                    {score && <span className="mts-card__score">⭐ {score}</span>}
                    {eps && <span>{eps}</span>}
                </div>
            </div>
        </div>
    );
}

// ── Main section ──────────────────────────────────────────────────
const MagazineTrailerSection = ({ genreId, type = 'new', size = 20 }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTrailer, setActiveTrailer] = useState(null); // item to show trailer for

    useEffect(() => {
        let url;
        if (genreId) {
            url = `/movies-public-api.php?type=trailer&genreId=${genreId}&page=0&size=${size}`;
        } else if (type === 'popular') {
            url = `/movies-public-api.php?type=trailer&order=popular&page=0&size=${size}`;
        } else {
            url = `/movies-public-api.php?type=trailer&page=0&size=${size}`;
        }
        setLoading(true);
        fetch(url)
            .then(r => r.json())
            .then(d => { if (d.success) setItems(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [genreId, type, size]);

    if (loading) return <div className="mts-loading">Đang tải...</div>;
    if (items.length < 2) return null;

    const [first, ...rest] = items;
    const smalls = rest.slice(0, 3);

    return (
        <>
            <div className="mts-grid">
                <BigCard item={first} onTrailer={setActiveTrailer} />
                {smalls.map((item, i) => (
                    <SmallCard key={item.id} item={item} rank={i + 2} onTrailer={setActiveTrailer} />
                ))}
            </div>

            {activeTrailer && (
                <TrailerModal item={activeTrailer} onClose={() => setActiveTrailer(null)} />
            )}
        </>
    );
};

export default MagazineTrailerSection;
